import React, { useState, useRef } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Container
} from '@mui/material';
import {
  QrCode2,
  Download,
  ContentCopy,
  Refresh,
  Link,
  TextFields,
  Wifi,
  Email,
  Palette,
  Settings,
  FormatSize,
  Margin
} from '@mui/icons-material';
import QRCode from 'qrcode';

const QRCodeGenerator = () => {
  const [text, setText] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({
    size: 300,
    margin: 2,
    darkColor: '#1976d2',
    lightColor: '#ffffff',
    errorCorrection: 'M'
  });

  const downloadRef = useRef(null);

  const generateQRCode = async () => {
    if (!text.trim()) {
      setError('Please enter some text or URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const qrOptions = {
        width: options.size,
        margin: options.margin,
        color: {
          dark: options.darkColor,
          light: options.lightColor
        },
        errorCorrectionLevel: options.errorCorrection
      };

      const qrDataUrl = await QRCode.toDataURL(text, qrOptions);
      setQrCode(qrDataUrl);
    } catch (err) {
      setError('Failed to generate QR code: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (qrCode && downloadRef.current) {
      const link = downloadRef.current;
      link.href = qrCode;
      link.download = `qrcode-${Date.now()}.png`;
      link.click();
    }
  };

  const copyToClipboard = async () => {
    if (qrCode) {
      try {
        const response = await fetch(qrCode);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        setError('');
      } catch (err) {
        setError('Failed to copy to clipboard');
      }
    }
  };

  const handleOptionChange = (key, value) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const presetColors = [
    { dark: '#1976d2', light: '#ffffff', name: 'Primary Blue' },
    { dark: '#2e7d32', light: '#f1f8e9', name: 'Green' },
    { dark: '#ed6c02', light: '#fff3e0', name: 'Orange' },
    { dark: '#d32f2f', light: '#ffebee', name: 'Red' },
    { dark: '#7b1fa2', light: '#f3e5f5', name: 'Purple' },
    { dark: '#0288d1', light: '#e1f5fe', name: 'Light Blue' },
  ];

  const sampleTexts = [
    { text: 'https://example.com', icon: <Link />, label: 'Website URL' },
    { text: 'Hello World! This is a sample text.', icon: <TextFields />, label: 'Plain Text' },
    { text: 'mailto:test@example.com', icon: <Email />, label: 'Email Address' },
    { text: 'WIFI:S:MyNetwork;T:WPA;P:Password;;', icon: <Wifi />, label: 'WiFi Credentials' }
  ];

  const errorCorrectionLevels = [
    { value: 'L', label: 'Low (7%)' },
    { value: 'M', label: 'Medium (15%)' },
    { value: 'Q', label: 'Quartile (25%)' },
    { value: 'H', label: 'High (30%)' }
  ];

  return (
    <Container>
      <Box sx={{ py: 4 }}>
        <br/><br/><br/>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
          <QrCode2 sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
            QR Code Generator
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Create custom QR codes for URLs, text, contacts, and more
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Input and Controls Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            {/* Text Input */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <TextFields color="primary" />
                Enter Text or URL
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter URL, text, email, WiFi credentials, etc..."
                variant="outlined"
                error={!!error}
                helperText={error}
              />
            </Box>

            {/* Sample Texts */}
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom color="text.secondary">
                Quick Examples:
              </Typography>
              <Grid container spacing={1}>
                {sampleTexts.map((sample, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Chip
                      icon={sample.icon}
                      label={sample.label}
                      onClick={() => setText(sample.text)}
                      variant="outlined"
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Generate Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={generateQRCode}
              disabled={loading}
              startIcon={loading ? <Refresh /> : <QrCode2 />}
              sx={{ mb: 3, py: 1.5 }}
            >
              {loading ? 'Generating QR Code...' : 'Generate QR Code'}
            </Button>

            <Divider sx={{ my: 3 }} />

            {/* Customization Options */}
            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
              <Settings color="primary" />
              Customization Options
            </Typography>

            {/* Size Slider */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FormatSize color="action" />
                <Typography variant="body2" color="text.secondary">
                  Size: {options.size}px
                </Typography>
              </Box>
              <Slider
                value={options.size}
                onChange={(e, value) => handleOptionChange('size', value)}
                min={100}
                max={500}
                step={10}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Margin Slider */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Margin color="action" />
                <Typography variant="body2" color="text.secondary">
                  Margin: {options.margin}
                </Typography>
              </Box>
              <Slider
                value={options.margin}
                onChange={(e, value) => handleOptionChange('margin', value)}
                min={0}
                max={10}
                step={1}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Error Correction */}
            <Box mb={3}>
              <FormControl fullWidth>
                <InputLabel>Error Correction Level</InputLabel>
                <Select
                  value={options.errorCorrection}
                  onChange={(e) => handleOptionChange('errorCorrection', e.target.value)}
                  label="Error Correction Level"
                >
                  {errorCorrectionLevels.map(level => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Color Presets */}
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                <Palette color="primary" />
                Color Presets
              </Typography>
              <Grid container spacing={1}>
                {presetColors.map((preset, index) => (
                  <Grid item xs={4} sm={2} key={index}>
                    <Tooltip title={preset.name}>
                      <IconButton
                        sx={{
                          width: '100%',
                          height: 40,
                          background: `linear-gradient(135deg, ${preset.dark} 50%, ${preset.light} 50%)`,
                          border: options.darkColor === preset.dark ? '2px solid' : '1px solid',
                          borderColor: options.darkColor === preset.dark ? 'primary.main' : 'divider',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                        onClick={() => {
                          setOptions(prev => ({
                            ...prev,
                            darkColor: preset.dark,
                            lightColor: preset.light
                          }));
                        }}
                      />
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Custom Colors */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Dark Color
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="color"
                    value={options.darkColor}
                    onChange={(e) => handleOptionChange('darkColor', e.target.value)}
                    style={{
                      width: 40,
                      height: 40,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {options.darkColor}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Light Color
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="color"
                    value={options.lightColor}
                    onChange={(e) => handleOptionChange('lightColor', e.target.value)}
                    style={{
                      width: 40,
                      height: 40,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {options.lightColor}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Output Section */}
        <Grid item xs={12} md={6}>
          {qrCode ? (
            <Paper elevation={3} sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom textAlign="center">
                Generated QR Code
              </Typography>
              
              <Box display="flex" justifyContent="center" mb={3}>
                <Card variant="outlined" sx={{ p: 2, bgcolor: options.lightColor }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                    <img 
                      src={qrCode} 
                      alt="Generated QR Code" 
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        borderRadius: 8
                      }}
                    />
                  </CardContent>
                </Card>
              </Box>

              {/* QR Code Info */}
              <Alert severity="success" sx={{ mb: 2 }}>
                QR Code generated successfully!
              </Alert>

              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Size: {options.size} × {options.size}px
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Error Correction: {options.errorCorrection}
                  </Typography>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Download />}
                    onClick={downloadQRCode}
                    size="large"
                  >
                    Download
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={copyToClipboard}
                    size="large"
                  >
                    Copy
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 6, 
                textAlign: 'center',
                bgcolor: 'grey.50',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <QrCode2 sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="grey.600">
                Your QR Code Will Appear Here
              </Typography>
              <Typography variant="body2" color="grey.500" textAlign="center">
                Enter text or URL in the input field and click "Generate QR Code" to create your custom QR code.
              </Typography>
              
              <Box mt={4} textAlign="left" width="100%">
                <Typography variant="subtitle2" gutterBottom color="grey.600">
                  Supported Content Types:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Link color="action" fontSize="small" />
                      <Typography variant="body2" color="grey.600">URLs & Websites</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextFields color="action" fontSize="small" />
                      <Typography variant="body2" color="grey.600">Plain Text</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email color="action" fontSize="small" />
                      <Typography variant="body2" color="grey.600">Email Addresses</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Wifi color="action" fontSize="small" />
                      <Typography variant="body2" color="grey.600">WiFi Credentials</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Hidden download link */}
      <a ref={downloadRef} style={{ display: 'none' }} />
    </Box>
    </Container>
  );
};

export default QRCodeGenerator;